import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import { storage } from '../utils/storage';

const API_URL = process.env.REACT_APP_API_URL || '';
const STORAGE_KEY = 'auth-storage';

type RetryableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

function forceLogout() {
  useAuthStore.getState().logout();
  window.location.href = '/login';
}

function retryRequest(request: RetryableRequest, token: string) {
  request._retry = true;
  request.headers.Authorization = `Bearer ${token}`;
  return api(request);
}

// Reads the refresh token directly from localStorage if another tab has already completed a token rotation.
function getPersistedRefreshToken(): string | null {
  const parsed = storage.getJSON<{ state?: { refreshToken?: string } }>(STORAGE_KEY);
  return parsed?.state?.refreshToken ?? null;
}

/**
 * If another tab refreshed while this tab was stale, rehydrate and return the
 * new access token. Returns null if storage has the same or there is no refresh token.
 */
function tryRecoverFromCrossTab(staleRefreshToken: string | null): string | null {
  const persisted = getPersistedRefreshToken();
  if (!persisted || persisted === staleRefreshToken) return null;

  useAuthStore.persist.rehydrate();
  return useAuthStore.getState().token;
}

const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token!)));
  failedQueue = [];
}

const UnrefreshableEndpoints = ['/auth/login', '/auth/register', '/microsoft/', '/auth/refresh'];
api.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequest;
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    const url = originalRequest.url || '';
    // Skip non-refreshable endpoints
    if (UnrefreshableEndpoints.some(endpoint => url.includes(endpoint))) {
      return Promise.reject(error);
    }
    // Logout if already retried
    if (originalRequest._retry) {
      forceLogout();
      return Promise.reject(error);
    }
    // If another refresh is in flight push request to the queue
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => resolve(retryRequest(originalRequest, token)),
          reject,
        });
      });
    }
    // If another tab already refreshed token - retry request
    const refreshToken = useAuthStore.getState().refreshToken;
    const crossTabToken = tryRecoverFromCrossTab(refreshToken);
    if (crossTabToken) {
      return retryRequest(originalRequest, crossTabToken);
    }

    // Refresh starts
    isRefreshing = true;
    originalRequest._retry = true;
    // Logout if there no refresh token
    if (!refreshToken) {
      isRefreshing = false;
      processQueue(error, null);
      forceLogout();
      return Promise.reject(error);
    }

    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refresh_token: refreshToken,
      });
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      useAuthStore.getState().setTokens(accessToken, newRefreshToken);
      // Re-run current request and those in queue
      processQueue(null, accessToken);
      return retryRequest(originalRequest, accessToken);
    } catch (refreshError) {
      // Try the last time if another tab may have won the race while we were waiting
      const recovered = tryRecoverFromCrossTab(refreshToken);
      if (recovered) {
        processQueue(null, recovered);
        return retryRequest(originalRequest, recovered);
      }

      processQueue(refreshError, null);
      forceLogout();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
